import { useState } from "react";
import { Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { act, useLoad } from "@/lib/hooks";
import { api, fdate, fdatetime } from "@/lib/api";

const DateChoice = ({ inv, mode, setMode }) => {
  const [s] = useLoad("/acct/settings");
  const own = inv.created_at.slice(0, 10);
  const locked = !!(s?.lock_date && own <= s.lock_date);
  const opts = [["today", "Date the cancellation today", "The reversal goes into this month. The original month still shows the sale."],
    ["invoice_month", `Date it back to the invoice's own month (${fdate(own)})`, locked ? `Not available: that month is locked up to ${fdate(s.lock_date)}.` : "The reversal goes into the month of the invoice, so that month no longer shows the sale."]];
  return (
    <fieldset className="space-y-2 text-sm" data-testid="invoice-cancel-dating">
      <legend className="mb-1 font-medium text-slate-700">How should the accounts date this cancellation?</legend>
      {opts.map(([k, l, h]) => {
        const off = k === "invoice_month" && (locked || !s);
        return <label key={k} className={`flex gap-2 rounded-md border p-2 ${mode === k ? "border-[#13294B] bg-slate-50" : "border-slate-200"} ${off ? "opacity-60" : "cursor-pointer"}`}>
          <input type="radio" name="cancel-dating" className="mt-1" data-testid={`invoice-cancel-date-${k}`} checked={mode === k} disabled={off} onChange={() => setMode(k)} />
          <span><span className="block font-medium text-slate-800">{l}{k === "today" ? " (default)" : ""}</span><span className="block text-xs text-slate-500">{h}</span></span></label>;
      })}
    </fieldset>
  );
};

export function CancelInvoiceButton({ inv, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("today");
  const settled = (inv.amount_paid || 0) > 0.004 || (inv.amount_credited || 0) > 0.004;
  const save = async () => { if (await act(() => api.post(`/tenant/invoices/${inv.id}/cancel`, { reason, date_mode: mode }), `${inv.number} cancelled`)) { setOpen(false); onDone(); } };
  return (
    <>
      <Button variant="outline" data-testid="invoice-cancel-button" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => { setMode("today"); setOpen(true); }}><Ban className="mr-1 h-4 w-4" />Cancel invoice</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invoice-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel {inv.number}</DialogTitle>
            <DialogDescription>{settled ? "This invoice has a payment or credit note. Reverse the payment first, then cancel." : "The invoice keeps its number and stays in the history. Its receivable, sales income, VAT and levies are reversed in the accounts, and any linked job can be invoiced again."}</DialogDescription>
          </DialogHeader>
          {!settled && <><DateChoice inv={inv} mode={mode} setMode={setMode} />
            <Textarea data-testid="invoice-cancel-reason" placeholder="Reason for cancelling (required)" value={reason} onChange={(e) => setReason(e.target.value)} /></>}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Keep invoice</Button>
            {!settled && <Button data-testid="invoice-cancel-confirm" disabled={reason.trim().length < 3} onClick={save} className="bg-rose-600 hover:bg-rose-700">Cancel invoice</Button>}</DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CancelledBanner({ inv }) {
  const back = inv.cancel_date_mode === "invoice_month";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-5 py-3 text-sm text-rose-800" data-testid="invoice-cancelled-banner">
      <span className="rounded border-2 border-rose-600 px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-rose-700">Cancelled</span>
      <span className="min-w-0 flex-1">Cancelled by {inv.cancelled_by} on {fdatetime(inv.cancelled_at)} · reason: {inv.cancel_reason}. Nothing is due on this invoice.
        <span className="block text-xs" data-testid="invoice-cancel-dated">{back ? `Accounts dated back to the invoice's own month (${fdate(inv.cancel_posting_date)}).` : `Accounts dated on the cancellation date${inv.cancel_posting_date ? ` (${fdate(inv.cancel_posting_date)})` : ""}.`}</span></span>
    </div>
  );
}
